import React from "react";
import { twMerge } from "tailwind-merge";

export function Label({
  className,
  children,
  ...rest
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={twMerge(
        "block text-sm font-semibold text-ink-primary mb-1.5",
        className
      )}
      {...rest}
    >
      {children}
    </label>
  );
}

const SELECT_CHEVRON =
  'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\' fill=\'none\'><path d=\'M4 6l4 4 4-4\' stroke=\'%239ca3af\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/></svg>")';

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, style, ...rest }, ref) => (
  <select
    ref={ref}
    className={twMerge(
      "w-full appearance-none rounded-[10px] border border-line-subtle bg-surface-inset pl-3.5 pr-9 py-2.5 text-sm text-ink-primary outline-none focus:border-brand-to focus:ring-2 focus:ring-brand-soft/40",
      className
    )}
    style={{
      backgroundImage: SELECT_CHEVRON,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 0.875rem center",
      backgroundSize: "0.9em",
      ...style,
    }}
    {...rest}
  >
    {children}
  </select>
));
Select.displayName = "Select";

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...rest }, ref) => (
  <textarea
    ref={ref}
    className={twMerge(
      "w-full rounded-[10px] border border-line-subtle bg-surface-inset px-3.5 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted outline-none focus:border-brand-to focus:ring-2 focus:ring-brand-soft/40",
      className
    )}
    {...rest}
  />
));
Textarea.displayName = "Textarea";

export function Alert({
  color = "info",
  className,
  children,
}: {
  color?: "info" | "failure" | "success";
  className?: string;
  children: React.ReactNode;
}) {
  const colors = {
    info: "bg-brand-gradient-soft border-brand-soft/50 text-ink-secondary",
    failure: "bg-red-500/10 border-red-500/40 text-red-600 dark:text-red-400",
    success:
      "bg-green-500/10 border-green-500/40 text-green-700 dark:text-green-400",
  };
  return (
    <div
      className={twMerge(
        "rounded-xl border px-4 py-3 text-sm",
        colors[color],
        className
      )}
    >
      {children}
    </div>
  );
}
